import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { POSManagerOperator } from 'src/app/models/pos-manager.model';
import {
  CreatePOSOperator,
  GetPOSOperators,
  CreatePOSOperatorSuccess,
  UpdatePOSOperator,
  UpdatePOSOperatorSuccess,
  DeletePOSOperator,
  DeletePOSOperatorSuccess
} from './pos-operator.action';
import { PosOperatorService } from 'src/app/services/pos-manager/pos-operator/pos-operator.service';
import { catchError, tap } from 'rxjs';
import { ExceptionError } from '../../error/error.action';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ToastController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';

export interface PosManagerOperatorStateModel {
  operators: POSManagerOperator[];
  isLoading: boolean;
}

@State<PosManagerOperatorStateModel>({
  name: 'operators',
  defaults: {
    isLoading: false,
    operators: []
  }
})
@Injectable({ providedIn: 'root' })
export class PosManagerOperatorState {
  constructor(
    private operatorService: PosOperatorService,
    private modalService: ModalService,
    private toastController: ToastController,
    private translocoService: TranslocoService
  ) {}

  @Action(GetPOSOperators)
  getOperators(ctx: StateContext<PosManagerOperatorStateModel>) {
    ctx.patchState({ isLoading: true });
    return this.operatorService.getOperators().pipe(
      tap((operators) => {
        //TODO: sort items before storing them
        ctx.patchState({ operators, isLoading: false });
      }),
      catchError(() =>
        ctx.dispatch(
          new ExceptionError(
            this.translocoService.translate('errors.errorFetchingOperators')
          )
        )
      )
    );
  }

  @Action(CreatePOSOperator)
  createOperators(
    ctx: StateContext<PosManagerOperatorStateModel>,
    { newOperator }: CreatePOSOperator
  ) {
    this.modalService.showLoadingModal(
      this.translocoService.translate('messages.saving')
    );
    return this.operatorService.createOperator(newOperator).pipe(
      tap(async (operator) => {
        const toast = await this.toastController.create({
          color: 'primary',
          duration: 2000,
          message: this.translocoService.translate(
            'messages.operatorSavedSuccess'
          ),
          position: 'top'
        });
        await toast.present();
        ctx.dispatch([
          new CreatePOSOperatorSuccess(operator),
          new GetPOSOperators()
        ]);
      }),
      catchError(async (err) => {
        await this.modalService.closeLoadingModal();
        let message = this.translocoService.translate(
          'errors.errorCreatingOperator'
        );
        if (err?.error?.error?.length) {
          message = err?.error?.error[0];
        }
        return ctx.dispatch(new ExceptionError(message));
      })
    );
  }

  @Action(UpdatePOSOperator)
  updateOperators(
    ctx: StateContext<PosManagerOperatorStateModel>,
    { operator }: UpdatePOSOperator
  ) {
    this.modalService.showLoadingModal(
      this.translocoService.translate('messages.saving')
    );
    return this.operatorService.updateOperator(operator).pipe(
      tap(async () => {
        const toast = await this.toastController.create({
          color: 'primary',
          duration: 2000,
          message: this.translocoService.translate(
            'messages.operatorSavedSuccess'
          ),
          position: 'top'
        });
        await toast.present();
        ctx.dispatch([new UpdatePOSOperatorSuccess(), new GetPOSOperators()]);
      }),
      catchError(async (err) => {
        await this.modalService.closeLoadingModal();
        let message = this.translocoService.translate(
          'errors.errorUpdatingOperator'
        );
        if (err?.error?.error?.length) {
          message = err?.error?.error[0];
        }
        return ctx.dispatch(new ExceptionError(message));
      })
    );
  }

  // ! Old behavior need re-login to update permissions
  // @Action(UpdatePOSOperatorSuccess)
  // updatePOSOperatorSuccess(
  //   { dispatch }: StateContext<PosManagerOperatorStateModel>,
  //   { operator }: UpdatePOSOperatorSuccess
  // ) {
  //   dispatch(
  //     new PatchAuthOperator((prev) => ({
  //       ...prev,
  //       permissions: {
  //         ...prev.permissions,
  //         view_pos_beta_reports_in_pos_manager:
  //           !!operator.can_view_pos_beta_reports_in_pos_manager
  //       }
  //     }))
  //   );
  // }

  @Action(DeletePOSOperator)
  deleteOperators(
    ctx: StateContext<PosManagerOperatorStateModel>,
    { id }: DeletePOSOperator
  ) {
    this.modalService.showLoadingModal(
      this.translocoService.translate('messages.deleting')
    );
    return this.operatorService.deleteOperator(id).pipe(
      tap(async () => {
        const toast = await this.toastController.create({
          color: 'primary',
          duration: 2000,
          message: this.translocoService.translate(
            'messages.operatorDeletedSuccess'
          ),
          position: 'top'
        });
        await toast.present();
        ctx.dispatch([new DeletePOSOperatorSuccess(), new GetPOSOperators()]);
      }),
      catchError(async (err) => {
        await this.modalService.closeLoadingModal();
        let message = this.translocoService.translate(
          'errors.errorDeletingOperator'
        );
        if (err?.error?.error?.length) {
          message = err?.error?.error[0];
        }
        return ctx.dispatch(new ExceptionError(message));
      })
    );
  }
}
